<template>
  <div class="min-h-full bg-transparent text-slate-900 dark:text-[#F1F5F9] pb-16">
    <Banner @goClicked="handleGoClicked" />
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Initial Loading State (for the very first load of the page) -->
      <div
        v-if="isInitialLoading"
        class="flex flex-col items-center justify-center min-h-[60vh] px-4"
      >
        <AppLoader
          variant="card"
          size="lg"
          message="Loading Faculty Directory"
          subtext="We're gathering all educator profiles and credentials..."
          color="indigo"
          icon="cap"
        />
      </div>
      <!-- Main Content (only show when not initially loading) -->
      <div v-else>
        <!-- Header - This component is responsible for the bulk delete button -->
        <HeaderTeacher
          :bannerGoClicked="bannerClicked"
          @search="searchQuery = $event"
          @add-new="addTeacher"
          @bulk-delete="bulkDeleteTeachers"
          :is-any-selected="isAnyTeacherSelected"
          :selected-count="selectedTeacherIds.length"
        />
        <!-- Bulk Delete Button (appears when teachers are selected) -->
        <div
          v-if="isAnyTeacherSelected"
          class="mt-4 mb-4 flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2.5 sm:gap-3 animate-fade-in"
        >
          <!-- 🟦 Order Cards Button -->
          <button
            @click="bulkOrderCards"
            :disabled="isLoading || isTableLoading"
            class="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-transparent text-xs sm:text-sm font-semibold rounded-xl shadow-xs text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <CreditCard class="w-4 h-4 mr-2" />
            Order Cards ({{ selectedTeacherIds.length }})
          </button>

          <!-- 🟥 Delete Selected Button -->
          <button
            @click="bulkDeleteTeachers"
            :disabled="isLoading || isTableLoading"
            class="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-transparent text-xs sm:text-sm font-semibold rounded-xl shadow-xs text-white bg-rose-600 hover:bg-rose-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <Trash2 class="w-4 h-4 mr-2" />
            Delete Selected ({{ selectedTeacherIds.length }})
          </button>
        </div>

        <!-- Teachers Table & Grid Container -->
        <div class="relative">
          <!-- Content Loading Overlay (for search, pagination, refresh) -->
          <AppLoader
            :isVisible="isTableLoading"
            variant="card"
            size="md"
            message="Updating Faculty..."
            subtext="Fetching matching teacher profiles"
          />

          <!-- Teachers Table (Desktop View) -->
          <div
            class="hidden md:block bg-white dark:bg-[#111C30] rounded-2xl shadow-sm border border-slate-200/90 dark:border-[#263449] overflow-hidden transition-all duration-200"
            :class="{ 'mt-6': !isAnyTeacherSelected }"
          >
            <!-- Clean Directory Header Bar (Matching Reference Image) -->
            <div
              class="p-4 sm:p-5 border-b border-slate-100 dark:border-[#263449] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-[#111C30]"
            >
              <div>
                <h3
                  class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight"
                >
                  Faculty Directory
                </h3>
                <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 font-normal">
                  Manage faculty profiles, assigned subjects, and credentials
                </p>
              </div>

              <div class="flex items-center gap-2">
                <span
                  v-if="selectedTeacherIds.length > 0"
                  class="text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/40 px-3 py-1 rounded-lg"
                >
                  {{ selectedTeacherIds.length }} Selected
                </span>
                <span
                  class="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-[#18243A] px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/50"
                >
                  {{ totalResults || teachers.length }} Faculty
                </span>
              </div>
            </div>

            <!-- Table Container -->
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-slate-200 dark:divide-[#263449]">
                <thead>
                  <tr
                    class="bg-slate-50/80 dark:bg-[#0F172A] border-b border-slate-200/80 dark:border-[#263449] text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    <th
                      scope="col"
                      class="py-3.5 pl-5 pr-3 text-left w-12"
                    >
                      <!-- Select All Checkbox -->
                      <div class="flex items-center">
                        <input
                          type="checkbox"
                          :checked="allSelected"
                          @change="toggleSelectAll"
                          :disabled="isLoading || isTableLoading"
                          class="rounded-md border-slate-300 dark:border-[#263449] text-indigo-600 shadow-xs focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed h-4 w-4 cursor-pointer transition-colors"
                        />
                      </div>
                    </th>
                    <th
                      scope="col"
                      class="px-4 py-3.5 text-left font-bold"
                    >
                      Teacher & ID
                    </th>
                    <th
                      scope="col"
                      class="px-4 py-3.5 text-left font-bold"
                    >
                      Role / Title
                    </th>
                    <th
                      scope="col"
                      class="px-4 py-3.5 text-left font-bold"
                    >
                      Subject
                    </th>
                    <th
                      scope="col"
                      class="px-4 py-3.5 text-left font-bold"
                    >
                      Email Address
                    </th>
                    <th
                      scope="col"
                      class="px-4 py-3.5 text-left font-bold"
                    >
                      Phone Number
                    </th>
                    <th
                      scope="col"
                      class="px-4 py-3.5 text-left font-bold"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-6 text-right font-bold"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-[#111C30] divide-y divide-slate-100 dark:divide-[#263449]">
                  <tr
                    v-for="teacher in teachers"
                    :key="teacher._id"
                    class="group transition-all duration-150"
                    :class="[
                      selectedTeacherIds.includes(teacher._id)
                        ? 'bg-indigo-50/40 dark:bg-indigo-950/40'
                        : 'hover:bg-slate-50/70 dark:hover:bg-[#18243A]',
                    ]"
                  >
                    <!-- Checkbox -->
                    <td class="py-3.5 pl-5 pr-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        :checked="selectedTeacherIds.includes(teacher._id)"
                        @change="toggleTeacherSelection(teacher._id)"
                        :disabled="isLoading || isTableLoading"
                        class="rounded-md border-slate-300 text-indigo-600 shadow-xs focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed h-4 w-4 cursor-pointer transition-colors"
                      />
                    </td>

                    <!-- Teacher Profile (Avatar + Name + ID) -->
                    <td
                      class="px-4 py-3.5 whitespace-nowrap cursor-pointer"
                      @click="viewTeacher(teacher)"
                    >
                      <div class="flex items-center gap-3.5">
                        <div class="relative shrink-0">
                          <div
                            class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-xs overflow-hidden ring-2 ring-slate-100 transition-transform group-hover:scale-105"
                            :class="
                              teacher.photo ? '' : getAvatarColor(teacher.name)
                            "
                          >
                            <img
                              v-if="teacher.photo"
                              :src="teacher.photo"
                              :alt="teacher.name"
                              class="w-full h-full object-cover"
                            />
                            <span
                              v-else
                              class="text-sm font-semibold tracking-wide"
                            >
                              {{ getInitial(teacher.name) }}
                            </span>
                          </div>
                          <!-- Status dot on avatar -->
                          <span
                            class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white dark:ring-[#111C30]"
                            :class="
                              teacher.status === 'active'
                                ? 'bg-emerald-500'
                                : 'bg-slate-400'
                            "
                          ></span>
                        </div>

                        <div class="min-w-0">
                          <div
                            class="flex items-center gap-1.5 font-semibold text-slate-900 dark:text-[#F1F5F9] group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors text-sm"
                          >
                            <span class="truncate">{{ teacher.name }}</span>
                          </div>
                          <div
                            class="text-[11px] text-slate-500 dark:text-[#7E8DA5] font-mono flex items-center gap-1 mt-0.5"
                          >
                            <span class="text-slate-400 dark:text-[#7E8DA5]">ID:</span>
                            <span class="font-medium text-slate-600 dark:text-[#A6B4C8]">{{
                              teacher.attendanceID || "—"
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <!-- Role -->
                    <td class="px-4 py-3.5 whitespace-nowrap">
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] capitalize border border-slate-200/70 dark:border-[#263449]"
                      >
                        <Briefcase
                          class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] shrink-0"
                        />
                        <span>{{ teacher.role || "Teacher" }}</span>
                      </span>
                    </td>

                    <!-- Subject -->
                    <td class="px-4 py-3.5 whitespace-nowrap">
                      <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-100/90 dark:border-indigo-900/40 shadow-2xs"
                      >
                        <BookOpen
                          class="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 shrink-0"
                        />
                        <span>{{ teacher.subject || "General" }}</span>
                      </span>
                    </td>

                    <!-- Email -->
                    <td class="px-4 py-3.5 whitespace-nowrap">
                      <a
                        v-if="teacher.email"
                        :href="'mailto:' + teacher.email"
                        class="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-[#A6B4C8] hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors group/link max-w-[200px]"
                        :title="teacher.email"
                      >
                        <Mail
                          class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] group-hover/link:text-indigo-500 shrink-0"
                        />
                        <span
                          class="truncate underline-offset-2 group-hover/link:underline"
                          >{{ teacher.email }}</span
                        >
                      </a>
                      <span
                        v-else
                        class="text-xs text-slate-400 dark:text-[#7E8DA5]"
                        >—</span
                      >
                    </td>

                    <!-- Phone -->
                    <td class="px-4 py-3.5 whitespace-nowrap">
                      <a
                        v-if="teacher.phone"
                        :href="'tel:' + teacher.phone"
                        class="inline-flex items-center gap-1.5 text-xs font-mono text-slate-600 dark:text-[#A6B4C8] hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors group/phone"
                        :title="teacher.phone"
                      >
                        <Phone
                          class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] group-hover/phone:text-indigo-500 shrink-0"
                        />
                        <span>{{ teacher.phone }}</span>
                      </a>
                      <span
                        v-else
                        class="text-xs text-slate-400 dark:text-[#7E8DA5]"
                        >—</span
                      >
                    </td>

                    <!-- Status -->
                    <td class="px-4 py-3.5 whitespace-nowrap">
                      <span
                        v-if="teacher.status === 'active'"
                        class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/90 dark:border-emerald-800/40 shadow-2xs"
                      >
                        <span
                          class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
                        ></span>
                        Active
                      </span>
                      <span
                        v-else
                        class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border border-rose-200/90 dark:border-rose-800/40 shadow-2xs"
                      >
                        <span
                          class="w-1.5 h-1.5 rounded-full bg-rose-500"
                        ></span>
                        {{
                          teacher.status === "passout"
                            ? "Inactive"
                            : teacher.status || "Inactive"
                        }}
                      </span>
                    </td>

                    <!-- Actions -->
                    <td
                      class="py-3.5 pl-4 pr-6 whitespace-nowrap text-right text-sm font-medium"
                    >
                      <div
                        class="inline-flex items-center gap-1 bg-slate-50/80 dark:bg-[#18243A] p-1 rounded-xl border border-slate-200/60 dark:border-[#263449] shadow-2xs"
                      >
                        <button
                          @click="viewTeacher(teacher)"
                          :disabled="isLoading || isTableLoading"
                          class="p-1.5 rounded-lg text-slate-500 dark:text-[#7E8DA5] hover:text-indigo-600 hover:bg-white dark:hover:bg-[#111C30] hover:shadow-xs transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="View Teacher Profile"
                        >
                          <Eye class="w-4 h-4" />
                        </button>
                        <button
                          @click="editTeacher(teacher)"
                          :disabled="isLoading || isTableLoading"
                          class="p-1.5 rounded-lg text-slate-500 dark:text-[#7E8DA5] hover:text-amber-600 hover:bg-white dark:hover:bg-[#111C30] hover:shadow-xs transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit Teacher"
                        >
                          <Edit class="w-4 h-4" />
                        </button>
                        <button
                          @click="deleteTeacher(teacher)"
                          :disabled="isLoading || isTableLoading"
                          class="p-1.5 rounded-lg text-slate-500 dark:text-[#7E8DA5] hover:text-rose-600 hover:bg-white dark:hover:bg-[#111C30] hover:shadow-xs transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete Teacher"
                        >
                          <Trash2 class="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  <!-- Empty State -->
                  <tr v-if="teachers.length === 0 && !isTableLoading">
                    <td
                      colspan="8"
                      class="px-6 py-16 text-center"
                    >
                      <div class="max-w-sm mx-auto flex flex-col items-center">
                        <div
                          class="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm mb-3"
                        >
                          <GraduationCap class="w-8 h-8" />
                        </div>
                        <h4 class="text-base font-bold text-slate-800">
                          No teachers found
                        </h4>
                        <p
                          class="text-xs text-slate-500 mt-1 max-w-xs leading-relaxed"
                        >
                          Try searching with different keywords or add a new
                          teacher to this directory.
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Teachers Grid (Mobile View) -->
          <div
            class="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 w-full"
            :class="{ 'mt-6': !isAnyTeacherSelected }"
          >
            <!-- Empty State -->
            <div
              v-if="teachers.length === 0 && !isTableLoading"
              class="col-span-full text-center py-12 px-6 bg-white dark:bg-[#111C30] rounded-2xl shadow-sm border border-slate-200/90 dark:border-[#263449]"
            >
              <div
                class="w-14 h-14 mx-auto mb-3 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-sm"
              >
                <GraduationCap class="w-7 h-7" />
              </div>
              <h4 class="text-base font-bold text-slate-800 dark:text-[#F1F5F9]">
                No teachers found
              </h4>
              <p
                class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-1 max-w-xs mx-auto leading-relaxed"
              >
                Try searching with different keywords or add a new teacher to
                this directory.
              </p>
            </div>

            <!-- Teacher Card (Premium Mobile Design) -->
            <div
              v-for="teacher in teachers"
              :key="teacher._id"
              class="group relative bg-white dark:bg-[#111C30] rounded-2xl border transition-all duration-200 overflow-hidden shadow-xs hover:shadow-md flex flex-col justify-between touch-manipulation"
              :class="[
                selectedTeacherIds.includes(teacher._id)
                  ? 'border-indigo-500 ring-2 ring-indigo-500/20 bg-indigo-50/25 dark:bg-indigo-950/40'
                  : 'border-slate-200/90 dark:border-[#263449] hover:border-slate-300 dark:hover:border-slate-600',
              ]"
            >
              <div class="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-3.5">
                <!-- Top Row: Avatar + Name & ID + Selection Checkbox -->
                <div class="flex items-start justify-between gap-3">
                  <div
                    class="flex items-center gap-3 min-w-0 cursor-pointer flex-1"
                    @click="viewTeacher(teacher)"
                  >
                    <!-- Avatar with status indicator -->
                    <div class="relative shrink-0">
                      <div
                        class="w-12 h-12 min-w-[48px] max-w-[48px] min-h-[48px] max-h-[48px] rounded-2xl flex items-center justify-center font-bold text-white shadow-sm overflow-hidden ring-2 ring-slate-100 dark:ring-slate-700/60"
                        :class="teacher.photo ? '' : getAvatarColor(teacher.name)"
                      >
                        <img
                          v-if="teacher.photo"
                          :src="teacher.photo"
                          :alt="teacher.name"
                          class="w-full h-full object-cover"
                        />
                        <span
                          v-else
                          class="text-base font-bold tracking-wide"
                        >
                          {{ getInitial(teacher.name) }}
                        </span>
                      </div>
                      <span
                        class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white dark:ring-[#111C30]"
                        :class="
                          teacher.status === 'active'
                            ? 'bg-emerald-500 animate-pulse'
                            : 'bg-slate-400'
                        "
                      ></span>
                    </div>

                    <!-- Teacher Name & Monospace ID -->
                    <div class="min-w-0 flex-1">
                      <h3
                        class="font-extrabold text-slate-900 dark:text-white text-sm sm:text-base truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors"
                      >
                        {{ teacher.name }}
                      </h3>
                      <div class="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span
                          class="inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-slate-600 dark:text-[#A6B4C8] bg-slate-100 dark:bg-[#18243A] px-2 py-0.5 rounded-md border border-slate-200/70 dark:border-[#263449]"
                        >
                          <Hash class="w-2.5 h-2.5 text-slate-400 dark:text-[#7E8DA5]" />
                          {{ teacher.attendanceID || "—" }}
                        </span>
                        <!-- Status Capsule -->
                        <span
                          v-if="teacher.status === 'active'"
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/90 dark:border-emerald-800/40"
                        >
                          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Active
                        </span>
                        <span
                          v-else
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border border-rose-200/90 dark:border-rose-800/40"
                        >
                          <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                          {{ teacher.status === "passout" ? "Inactive" : teacher.status || "Inactive" }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Selection Checkbox with Safe Hit Box -->
                  <div class="pt-0.5" @click.stop>
                    <label class="p-2 -m-2 flex items-center justify-center cursor-pointer select-none">
                      <input
                        type="checkbox"
                        :checked="selectedTeacherIds.includes(teacher._id)"
                        @change="toggleTeacherSelection(teacher._id)"
                        :disabled="isLoading || isTableLoading"
                        class="rounded-lg border-slate-300 dark:border-slate-600 text-indigo-600 shadow-xs focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed h-5 w-5 cursor-pointer transition-colors"
                      />
                    </label>
                  </div>
                </div>

                <!-- Badges Row: Role & Subject -->
                <div class="flex flex-wrap items-center gap-1.5">
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#CBD5E1] capitalize border border-slate-200/70 dark:border-[#263449]"
                  >
                    <Briefcase class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <span>{{ teacher.role || "Teacher" }}</span>
                  </span>

                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/40 shadow-2xs truncate max-w-[170px]"
                  >
                    <BookOpen class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <span class="truncate">{{ teacher.subject || "General" }}</span>
                  </span>
                </div>

                <!-- Info Grid: Email, Phone, DOB & Age, Aadhaar -->
                <div
                  class="bg-slate-50/80 dark:bg-[#18243A]/60 rounded-xl p-3 border border-slate-100 dark:border-[#263449] space-y-2 text-xs"
                >
                  <!-- Email -->
                  <div class="flex items-center gap-2">
                    <Mail class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] shrink-0" />
                    <a
                      v-if="teacher.email"
                      :href="'mailto:' + teacher.email"
                      class="text-slate-600 dark:text-[#A6B4C8] hover:text-indigo-600 dark:hover:text-indigo-400 truncate underline-offset-2 hover:underline transition-colors flex-1"
                      :title="teacher.email"
                    >
                      {{ teacher.email }}
                    </a>
                    <span v-else class="text-slate-400 dark:text-[#7E8DA5]">—</span>
                  </div>

                  <!-- Phone -->
                  <div class="flex items-center gap-2">
                    <Phone class="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <a
                      v-if="teacher.phone"
                      :href="'tel:' + teacher.phone"
                      class="font-mono font-medium text-slate-700 dark:text-[#CBD5E1] hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex-1"
                    >
                      {{ teacher.phone }}
                    </a>
                    <span v-else class="text-slate-400 dark:text-[#7E8DA5]">—</span>
                  </div>

                  <!-- Date of Birth & Calculated Age -->
                  <div
                    v-if="teacher.dob"
                    class="flex items-center gap-2 text-slate-600 dark:text-[#A6B4C8]"
                  >
                    <Cake class="w-3.5 h-3.5 text-pink-500 shrink-0" />
                    <span>Born {{ formatDate(teacher.dob) }}</span>
                    <span
                      v-if="calculateAge(teacher.dob)"
                      class="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-pink-50 dark:bg-pink-950/40 text-pink-700 dark:text-pink-300 border border-pink-200/60 dark:border-pink-800/40"
                    >
                      {{ calculateAge(teacher.dob) }} yrs
                    </span>
                  </div>

                  <!-- Aadhaar Number (if available) -->
                  <div
                    v-if="teacher.aadhaarNumber"
                    class="flex items-center gap-2 text-slate-600 dark:text-[#A6B4C8]"
                  >
                    <ShieldCheck class="w-3.5 h-3.5 text-purple-500 shrink-0" />
                    <span class="text-slate-400 dark:text-[#7E8DA5]">Aadhaar:</span>
                    <span class="font-mono font-semibold text-slate-700 dark:text-[#CBD5E1]">
                      {{ teacher.aadhaarNumber }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action Buttons Dock -->
              <div
                class="px-3.5 py-3 bg-slate-50/70 dark:bg-[#0F172A] border-t border-slate-100 dark:border-[#263449] flex items-center gap-2"
              >
                <!-- View Profile Button (Primary) -->
                <button
                  @click="viewTeacher(teacher)"
                  :disabled="isLoading || isTableLoading"
                  class="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-xs active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[38px]"
                >
                  <Eye class="w-3.5 h-3.5" />
                  <span>View Profile</span>
                </button>

                <!-- Quick Call Button (if phone exists) -->
                <a
                  v-if="teacher.phone"
                  :href="'tel:' + teacher.phone"
                  class="inline-flex items-center justify-center p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 text-xs font-semibold shadow-2xs border border-emerald-200/80 dark:border-emerald-800/40 active:scale-[0.98] transition-all min-h-[38px] min-w-[38px]"
                  title="Call Teacher"
                >
                  <PhoneCall class="w-3.5 h-3.5" />
                </a>

                <!-- Edit Button -->
                <button
                  @click="editTeacher(teacher)"
                  :disabled="isLoading || isTableLoading"
                  class="inline-flex items-center justify-center p-2.5 rounded-xl bg-white dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#172235] text-slate-700 dark:text-[#F1F5F9] text-xs font-semibold shadow-2xs border border-slate-200/80 dark:border-[#263449] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[38px] min-w-[38px]"
                  title="Edit Teacher"
                >
                  <Edit class="w-3.5 h-3.5" />
                </button>

                <!-- Delete Button -->
                <button
                  @click="deleteTeacher(teacher)"
                  :disabled="isLoading || isTableLoading"
                  class="inline-flex items-center justify-center p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 text-xs font-semibold shadow-2xs border border-rose-200/80 dark:border-rose-900/40 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[38px] min-w-[38px]"
                  title="Delete Teacher"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Pagination Controls -->
        <div
          v-if="totalResults > 0 && totalPages > 1"
          class="flex justify-center items-center space-x-3 mt-8"
        >
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1 || isLoading || isTableLoading"
            class="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-[#263449] rounded-lg text-gray-700 dark:text-[#A6B4C8] bg-white dark:bg-[#111C30] hover:bg-gray-50 dark:hover:bg-[#18243A] dark:hover:text-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            <ChevronLeft
              class="w-4 h-4 text-blue-600 dark:text-blue-400"
              aria-hidden="true"
            />
            <span>Previous</span>
          </button>
          <span class="text-gray-700 dark:text-[#F1F5F9] font-medium">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="
              currentPage === totalPages || isLoading || isTableLoading
            "
            class="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-[#263449] rounded-lg text-gray-700 dark:text-[#A6B4C8] bg-white dark:bg-[#111C30] hover:bg-gray-50 dark:hover:bg-[#18243A] dark:hover:text-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            <span>Next</span>
            <ChevronRight
              class="w-4 h-4 text-blue-600 dark:text-blue-400"
              aria-hidden="true"
            />
          </button>
        </div>
        <!-- Delete Confirmation Modal (Inline for simplicity, consider a dedicated modal component for complex UIs) -->
        <div
          v-if="showDeleteModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 sm:p-6"
          @click.self="cancelDelete"
        >
          <div
            class="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-md transform transition-all duration-300 ease-in-out scale-95 opacity-0"
            :class="{ 'scale-100 opacity-100': showDeleteModal }"
          >
            <h2 class="text-2xl font-bold text-gray-900 mb-4">
              Confirm Deletion
            </h2>
            <p
              v-if="teacherToDelete"
              class="text-gray-700 mb-6"
            >
              Are you sure you want to delete teacher
              <span class="font-semibold text-red-600">{{
                teacherToDelete.name
              }}</span>
            </p>
            <p
              v-else
              class="text-gray-700 mb-6"
            >
              Are you sure you want to delete the selected
              <span class="font-semibold text-red-600">{{
                selectedTeacherIds.length
              }}</span>
              teachers? This action cannot be undone.
            </p>
            <div class="flex justify-end space-x-3">
              <button
                @click="cancelDelete"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-gray-200 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="confirmDelete"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  v-if="isLoading"
                  class="flex items-center"
                >
                  <svg
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Deleting...
                </span>
                <span v-else>Delete</span>
              </button>
            </div>
          </div>
        </div>
        <!-- 🟦 Order Cards Confirmation Modal -->
        <div
          v-if="showOrderModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 sm:p-6"
          @click.self="cancelOrderCards"
        >
          <div
            class="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-md transform transition-all duration-300 ease-in-out scale-95 opacity-0"
            :class="{ 'scale-100 opacity-100': showOrderModal }"
          >
            <h2 class="text-2xl font-bold text-gray-900 mb-4">
              Confirm Smart Card Order
            </h2>
            <!-- Important notice box -->
            <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p class="text-sm text-blue-900">
                <span class="font-semibold">⚠️ Important:</span> Only teacher
                with status
                <span class="font-bold text-blue-700">ACTIVE</span> card will be
                booked.
              </p>
            </div>
            <p class="text-gray-700 mb-6 text-sm">
              Are you sure you want to order
              <span class="font-semibold text-blue-600">{{
                selectedTeacherIds.length
              }}</span>
              smart card(s) for the selected teachers?
            </p>
            <div class="flex justify-end space-x-3">
              <button
                @click="cancelOrderCards"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-gray-200 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="confirmOrderCards"
                :disabled="isLoading"
                class="inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  v-if="isLoading"
                  class="flex items-center"
                >
                  <svg
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Ordering...
                </span>
                <span v-else>Confirm Order</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Toast Notification (Integrated) -->
      <transition name="fade">
        <div
          v-if="toast.isVisible"
          :class="toastClasses"
          class="fixed bottom-6 right-6 p-4 rounded-lg shadow-xl text-white text-base font-medium flex items-center space-x-2 min-w-[250px] z-[100000]"
        >
          <Info
            v-if="toast.type === 'info'"
            class="w-5 h-5"
          />
          <CheckCircle
            v-if="toast.type === 'success'"
            class="w-5 h-5"
          />
          <XCircle
            v-if="toast.type === 'error'"
            class="w-5 h-5"
          />
          <span>{{ toast.message }}</span>
        </div>
      </transition>
      <!-- Teacher Details Modal -->
      <TeacherDetailsModal
        :teacher="selectedTeacher"
        :is-open="showViewModal"
        @close="showViewModal = false"
      />
      <!-- Teacher Edit Modal -->
      <TeacherEditModal
        :teacher="selectedTeacher"
        :is-open="showEditModal"
        @close="showEditModal = false"
        @save="handleSaveEdit"
        :isLoading="isLoading"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, reactive, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import { useRoute } from "vue-router";
  import {
    Eye,
    Edit,
    Trash2,
    Info,
    CheckCircle,
    XCircle,
    Briefcase,
    BookOpen,
    Mail,
    Phone,
    PhoneCall,
    CreditCard,
    ChevronLeft,
    ChevronRight,
    GraduationCap,
    Cake,
    Hash,
    ShieldCheck,
  } from "lucide-vue-next";
  import HeaderTeacher from "@/components/Teacher/HeaderTeacher.vue";
  import TeacherDetailsModal from "@/components/Teacher/Actions/TeacherDetailsModal.vue";
  import TeacherEditModal from "@/components/Teacher/Actions/TeacherEditModal.vue";
  import Banner from "@/components/Banner/TeacherBanner.vue";
  const bannerClicked = ref(false);
  function handleGoClicked(value) {
    bannerClicked.value = value;
  }

  // Vuex store
  const store = useStore();
  const route = useRoute();
  // UI state
  const searchQuery = ref("");
  const showModal = ref(false);
  const showViewModal = ref(false);
  const showEditModal = ref(false);
  const showDeleteModal = ref(false);
  const showOrderModal = ref(false);

  const isEditing = ref(false);
  const selectedTeacher = ref(null);
  const teacherToDelete = ref(null);
  const selectedTeacherIds = ref([]);
  const isLoading = ref(false); // This ref controls the loading state for save/delete operations
  const isInitialLoading = ref(true); // For the very first page load
  const isTableLoading = ref(false); // For search, pagination, and refresh of the table content

  // Pagination state
  const currentPage = ref(1);
  const itemsPerPage = ref(10);

  const toast = reactive({
    isVisible: false,
    message: "",
    type: "info",
  });
  let toastTimeoutId = null;

  // Form data (currentTeacher is now less critical as edit modal manages its own local state)
  const currentTeacher = reactive({
    id: "",
    name: "",
    role: "teacher",
    subject: "",
    email: "",
    phone: "",
    status: "active",
    photo: "",
    dob: "",
  });

  // Helper to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper to calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return "";
    try {
      const diff = Date.now() - new Date(dob).getTime();
      const ageDate = new Date(diff);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    } catch (e) {
      return "";
    }
  };

  // Fetch teachers with pagination and search parameters
  const fetchTeachers = async (
    page = currentPage.value,
    limit = itemsPerPage.value,
    search = searchQuery.value,
  ) => {
    // Set loading state based on whether it's the initial load or a content refresh
    if (isInitialLoading.value) {
      // Keep isInitialLoading true
    } else {
      isTableLoading.value = true; // For subsequent loads (search, pagination)
    }

    try {
      currentPage.value = page; // Update current page before dispatching
      await store.dispatch("fetchTeachers", { page, limit, search });
    } catch (error) {
      console.error("Error fetching teachers:", error);
      showToast("❌ Failed to load teachers", "error");
    } finally {
      isInitialLoading.value = false; // Always set to false after first load attempt
      isTableLoading.value = false; // Always set to false after content load attempt
    }
  };

  onMounted(async () => {
    await fetchTeachers(
      currentPage.value,
      itemsPerPage.value,
      searchQuery.value,
    );

    // 🔥 If coming from Smart Card page
    const userId = route.query.userId;

    if (userId) {
      const teacher = teachers.value.find((t) => t._id === userId);

      if (teacher) {
        // Already in list → open modal
        selectedTeacher.value = teacher;
        showViewModal.value = true;
      } else {
        // Not in current page → fetch directly
        const res = await store.dispatch("fetchUserById", userId);

        selectedTeacher.value = res;
        showViewModal.value = true;
      }
    }
  });

  // Get teachers and pagination info from Vuex
  const teachers = computed(() => store.getters.getAllTeachers || []);
  const totalResults = computed(
    () => store.getters.getTeacherPagination?.totalResults || 0,
  );
  const totalPages = computed(
    () => store.getters.getTeacherPagination?.totalPages || 0,
  );

  // Watch for search query changes and refetch data
  let searchTimeout = null;
  watch(searchQuery, (newQuery) => {
    clearTimeout(searchTimeout);
    isTableLoading.value = true; // Show loading immediately on search input
    searchTimeout = setTimeout(() => {
      fetchTeachers(1, itemsPerPage.value, newQuery); // Reset to page 1 on new search
    }, 300); // Debounce search input
  });

  // Watch for changes in teachers data (e.g., after delete/edit) to clear selections
  watch(teachers, () => {
    selectedTeacherIds.value = [];
  });

  // Pagination actions
  const goToPage = (page) => {
    if (
      page >= 1 &&
      page <= totalPages.value &&
      !isLoading.value &&
      !isTableLoading.value
    ) {
      fetchTeachers(page, itemsPerPage.value, searchQuery.value);
    }
  };

  // Checkbox logic for current page
  const allSelected = computed(() => {
    return (
      teachers.value.length > 0 &&
      teachers.value.every((t) => selectedTeacherIds.value.includes(t._id))
    );
  });

  const isAnyTeacherSelected = computed(
    () => selectedTeacherIds.value.length > 0,
  );

  // Toast classes
  const toastClasses = computed(() => {
    const baseClasses = "transition-all duration-300 ease-in-out";
    switch (toast.type) {
      case "success":
        return `${baseClasses} bg-green-600`;
      case "error":
        return `${baseClasses} bg-red-600`;
      case "info":
      default:
        return `${baseClasses} bg-blue-600`;
    }
  });

  // Toast utility
  const showToast = (msg, type = "info", duration = 3000) => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toast.message = msg;
    toast.type = type;
    toast.isVisible = true;
    toastTimeoutId = setTimeout(() => {
      toast.isVisible = false;
    }, duration);
  };

  // Reset form
  const resetCurrentTeacher = () => {
    Object.assign(currentTeacher, {
      id: "",
      name: "",
      role: "teacher",
      subject: "",
      email: "",
      phone: "",
      status: "active",
      photo: "",
      dob: "",
    });
  };

  // Actions
  const addTeacher = () => {
    isEditing.value = false;
    resetCurrentTeacher();
    showModal.value = true;
  };

  const editTeacher = (teacher) => {
    selectedTeacher.value = teacher;
    showEditModal.value = true;
  };

  const viewTeacher = (teacher) => {
    selectedTeacher.value = teacher;
    showViewModal.value = true;
  };

  const deleteTeacher = (teacher) => {
    teacherToDelete.value = teacher;
    showDeleteModal.value = true;
  };

  const bulkDeleteTeachers = () => {
    teacherToDelete.value = null;
    showDeleteModal.value = true;
  };

  const confirmDelete = async () => {
    isLoading.value = true;
    try {
      if (teacherToDelete.value) {
        await store.dispatch("deleteUserById", teacherToDelete.value._id);
        showToast(`🗑️ Deleted: ${teacherToDelete.value.name}`, "success");
      } else if (selectedTeacherIds.value.length > 0) {
        for (const id of selectedTeacherIds.value) {
          await store.dispatch("deleteUserById", id);
        }
        showToast(
          `🗑️ ${selectedTeacherIds.value.length} teachers deleted`,
          "success",
        );
        selectedTeacherIds.value = [];
      }
      showDeleteModal.value = false;
      await fetchTeachers(
        currentPage.value,
        itemsPerPage.value,
        searchQuery.value,
      ); // Re-fetch current page after delete
    } catch (err) {
      showToast("❌ Failed to delete teacher(s)", "error");
    } finally {
      isLoading.value = false;
      teacherToDelete.value = null;
    }
  };

  const cancelDelete = () => {
    showDeleteModal.value = false;
    teacherToDelete.value = null;
  };
  const bulkOrderCards = () => {
    if (selectedTeacherIds.value.length === 0) {
      showToast("Please select at least one teacher", "info");
      return;
    }
    showOrderModal.value = true; // Show confirmation modal
  };
  const confirmOrderCards = async () => {
    isLoading.value = true;
    try {
      const res = await store.dispatch("bookSmartCard", {
        userId: selectedTeacherIds.value,
      });

      if (res?.createdCards?.length > 0) {
        showToast(
          `✅ Smart cards ordered for ${res.createdCards.length} teacher(s)`,
          "success",
        );
      } else if (res?.skippedUsers?.length > 0) {
        showToast(
          `⚠️ Smart cards already exist for selected teacher(s)`,
          "warning",
        );
      } else {
        showToast(
          `✅ Smart card order processed`,
          "success",
        );
      }
      selectedTeacherIds.value = [];
      showOrderModal.value = false;
      await fetchTeachers(
        currentPage.value,
        itemsPerPage.value,
        searchQuery.value,
      );
    } catch (error) {
      console.error("Error ordering smart cards:", error);
      const backendError = error.response?.data?.error;
      if (backendError) {
        showToast(`❌ ${backendError}`, "error");
      } else {
        showToast("❌ Failed to order smart cards", "error");
      }
    } finally {
      isLoading.value = false;
    }
  };

  const cancelOrderCards = () => {
    showOrderModal.value = false;
  };

  // Handle save from TeacherEditModal
  const handleSaveEdit = async ({ teacherData }) => {
    isLoading.value = true; // Set loading true when save starts
    try {
      const originalTeacher = selectedTeacher.value;
      const updatesToSend = {};

      // Helper to normalize values that might be empty objects to empty strings
      const normalizeValue = (value) => {
        return typeof value === "object" &&
          value !== null &&
          Object.keys(value).length === 0
          ? "" // Treat empty object as empty string
          : value;
      };

      // 1. Handle regular fields (non-file related)
      for (const key in teacherData) {
        if (
          key === "_id" ||
          key === "photo" ||
          key === "aadhaarCard" ||
          key === "qualifications"
        ) {
          continue; // Handle these separately
        }
        const normalizedNewValue = normalizeValue(teacherData[key]);
        if (normalizedNewValue !== originalTeacher[key]) {
          updatesToSend[key] = normalizedNewValue;
        }
      }

      // 2. Handle photo (now expected to be a Base64 string or URL in teacherData.photo)
      const normalizedNewPhotoValue = normalizeValue(teacherData.photo);
      if (normalizedNewPhotoValue !== originalTeacher.photo) {
        updatesToSend.photo = normalizedNewPhotoValue;
      }

      // 3. Handle aadhaarCard (now expected to be a Base64 string or URL in teacherData.aadhaarCard)
      const normalizedNewAadhaarValue = normalizeValue(teacherData.aadhaarCard);
      if (normalizedNewAadhaarValue !== originalTeacher.aadhaarCard) {
        updatesToSend.aadhaarCard = normalizedNewAadhaarValue;
      }

      // 4. Handle qualifications
      const updatedQualifications = teacherData.qualifications
        ? JSON.parse(JSON.stringify(teacherData.qualifications))
        : [];
      let qualificationsChanged = false;

      // Normalize fileUrl for each qualification and check for changes
      updatedQualifications.forEach((qual, index) => {
        const normalizedFileUrl = normalizeValue(qual.fileUrl);
        // Compare with original qualification's fileUrl
        if (
          originalTeacher.qualifications &&
          originalTeacher.qualifications[index]
        ) {
          if (
            normalizedFileUrl !== originalTeacher.qualifications[index].fileUrl
          ) {
            qual.fileUrl = normalizedFileUrl;
            qualificationsChanged = true;
          }
        } else if (normalizedFileUrl !== "") {
          // New qualification with a fileUrl
          qualificationsChanged = true;
        }
      });

      // Check if the array structure itself changed (e.g., added/removed qualifications)
      if (
        updatedQualifications.length !==
        (originalTeacher.qualifications
          ? originalTeacher.qualifications.length
          : 0)
      ) {
        qualificationsChanged = true;
      }

      // If qualificationsChanged is true OR the array content is different, include it.
      if (
        qualificationsChanged ||
        JSON.stringify(updatedQualifications) !==
          JSON.stringify(originalTeacher.qualifications)
      ) {
        updatesToSend.qualifications = updatedQualifications;
      }

      // If no updates were detected, show an info toast and exit
      if (Object.keys(updatesToSend).length === 0) {
        showToast("ℹ️ No changes detected.", "info");
        showEditModal.value = false;
        return; // Exit early, no need to set isLoading to false in finally if we return here
      }

      await store.dispatch("editTeacherById", {
        teacherId: originalTeacher._id,
        updates: updatesToSend,
      });

      showToast(
        `✅ Teacher ${originalTeacher.name} updated successfully!`,
        "success",
      );
      await fetchTeachers(
        currentPage.value,
        itemsPerPage.value,
        searchQuery.value,
      );
    } catch (error) {
      console.error("Error saving teacher:", error);

      // 🔹 Show backend error (like email/phone already exists)
      const backendError = error.response?.data?.error;
      if (backendError) {
        showToast(`❌ ${backendError}`, "error");
      } else {
        showToast("❌ Failed to save teacher updates", "error");
      }
    } finally {
      isLoading.value = false; // Set loading false when save completes (success or error)
      showEditModal.value = false;
      selectedTeacher.value = null;
    }
  };

  // Toggle selection for all teachers on the current page
  const toggleSelectAll = () => {
    if (allSelected.value) {
      selectedTeacherIds.value = [];
    } else {
      selectedTeacherIds.value = teachers.value.map((t) => t._id);
    }
  };

  // Toggle selection for an individual teacher
  const toggleTeacherSelection = (id) => {
    const index = selectedTeacherIds.value.indexOf(id);
    if (index === -1) {
      selectedTeacherIds.value.push(id);
    } else {
      selectedTeacherIds.value.splice(index, 1);
    }
  };
  const getInitial = (name) => {
    if (!name) return "?";
    return name.charAt(0).toUpperCase();
  };
  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-amber-500",
    ];
    if (!name) return "bg-gray-400";
    return colors[name.charCodeAt(0) % colors.length];
  };
</script>

<style scoped>
  /* Toast transition styles */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }
  /* Basic spinner animation */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  /* Fade-in animation for bulk delete button */
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.3s ease-out forwards;
  }
</style>
